import classNames from 'classnames';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Modal } from '../components/Modal';
import type { ModalAction } from '../components/Modal';
import { focusInitial } from '../components/Modal/focus';

import styles from './ModalProvider.module.css';
import type { ModalStorage } from './storage';
import { modalStore } from './store';
import type { ModalEntry } from './store';

export interface ModalProviderProps {
  children?: ReactNode;
  /** Storage adapter backing `Don't show again`. Defaults to a guarded `localStorage` adapter. */
  storage?: ModalStorage;
}

function isDismissible(entry: ModalEntry | undefined): boolean {
  return entry != null && entry.options.dismissible !== false;
}

/**
 * Mounts the modal stack. Render once near the app root. Owns the single shared backdrop,
 * stacking (lower modals dim behind a scrim and go `inert`), focus (moves into the top modal,
 * restores on empty), Escape and overlay-click (both close the top modal — repeated clicks
 * unwind the stack like a queue) and body scroll-lock.
 */
export function ModalProvider({ children, storage }: ModalProviderProps) {
  const stack = useSyncExternalStore(
    modalStore.subscribe,
    modalStore.getSnapshot,
    modalStore.getSnapshot,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (storage != null) modalStore.setStorage(storage);
  }, [storage]);

  const hasModals = stack.length > 0;
  const topId = stack[stack.length - 1]?.id ?? null;

  // Remember the pre-modal focus while any modal is open; restore it once the stack empties.
  useEffect(() => {
    if (hasModals && restoreRef.current == null) {
      restoreRef.current = document.activeElement as HTMLElement | null;
    } else if (!hasModals && restoreRef.current != null) {
      restoreRef.current.focus();
      restoreRef.current = null;
    }
  }, [hasModals]);

  // Move focus into whichever modal is on top.
  useEffect(() => {
    if (topId == null) return;
    const card = rootRef.current?.querySelector<HTMLElement>(
      `[data-modal-id="${topId}"] [role="dialog"]`,
    );
    focusInitial(card ?? null);
  }, [topId]);

  // Lock page scroll while modals are open.
  useEffect(() => {
    if (!hasModals) return undefined;
    const { body } = document;
    const prior = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prior;
    };
  }, [hasModals]);

  // Escape closes the top modal (queue-unwind), unless it opted out.
  useEffect(() => {
    if (topId == null) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const current = modalStore.getSnapshot();
      if (isDismissible(current[current.length - 1])) modalStore.closeTop();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [topId]);

  const dismissTop = () => {
    if (isDismissible(stack[stack.length - 1])) modalStore.closeTop();
  };

  return (
    <>
      {children}
      {hasModals && typeof document !== 'undefined'
        ? createPortal(
            <div ref={rootRef} className={styles.root}>
              {/* Shared backdrop (aria-hidden, so no a11y interaction warning). Escape mirrors
                  this click for keyboard users. */}
              <div
                className={styles.backdrop}
                data-modal-backdrop
                aria-hidden="true"
                onClick={dismissTop}
              />
              {stack.map((entry, index) => {
                const isTop = index === stack.length - 1;
                const { options } = entry;
                const actions: ModalAction[] = (options.actions ?? []).map((action) => ({
                  ...action,
                  onClick: () => {
                    action.onClick?.();
                    // `closeOnClick: false` keeps the modal open (e.g. to stack another on top).
                    if (action.closeOnClick !== false) {
                      modalStore.close(entry.id, { action: action.value ?? action.label });
                    }
                  },
                }));
                return (
                  <div
                    key={entry.id}
                    data-modal-id={entry.id}
                    className={styles.layer}
                    aria-hidden={isTop ? undefined : true}
                    inert={isTop ? undefined : true}
                  >
                    <div className={styles.cardBox}>
                      <Modal
                        managed
                        className={classNames(styles.card)}
                        title={options.title}
                        description={options.description}
                        icon={options.icon}
                        body={options.body}
                        actions={actions}
                        dontShowAgainChecked={entry.dontShowAgainChecked}
                        onDontShowAgainChange={(checked) => {
                          modalStore.setDontShowAgain(entry.id, checked);
                        }}
                        onClose={() => {
                          modalStore.close(entry.id);
                        }}
                        {...(options.variant != null ? { variant: options.variant } : {})}
                        {...(options.headerLayout != null
                          ? { headerLayout: options.headerLayout }
                          : {})}
                        {...(options.footerLayout != null
                          ? { footerLayout: options.footerLayout }
                          : {})}
                        {...(options.showClose != null ? { showClose: options.showClose } : {})}
                        {...(options.dismissible != null
                          ? { dismissible: options.dismissible }
                          : {})}
                        {...(options.dontShowAgain != null
                          ? { dontShowAgain: options.dontShowAgain }
                          : {})}
                        {...(options.settings != null ? { settings: options.settings } : {})}
                        {...(options.labels != null ? { labels: options.labels } : {})}
                      />
                      {/* Purely-visual scrim over the card of a lower modal only — it must not
                        cover the whole viewport, so the shared backdrop never compounds. */}
                      {isTop ? null : <div className={styles.scrim} aria-hidden="true" />}
                    </div>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
